/**
 * Тексты лидерборда: заголовки табов, подписи под ними и единицы метрики.
 *
 * Общие для двух экранов:
 *   - клубный лидерборд `/habits/:id/leaderboard` (4 таба);
 *   - глобальный рейтинг клубов `/leaderboards` (3 таба, без «Характеристика»).
 *
 * Семантика таба «Серии» (этап 5, материализованная серия):
 * `metric_value` — ТЕКУЩАЯ серия дней подряд (`memberships.current_streak`),
 * а не суммарное число чек-инов за всё время. Поэтому в подписи явно
 * сказано «текущая серия» и «дней подряд».
 *
 * Единица измерения для «Серии» — «дн.» (как в TodayPage: «дн. подряд»).
 */
import type { LeaderboardTabId } from "@/shared/types";

export interface LeaderboardTabText {
  /** Название таба/секции («Серии», «Охотники», «Лентяи», «Характеристика»). */
  label: string;
  /** Иконка таба. */
  emoji: string;
}

export const LEADERBOARD_TAB_TEXTS: Record<LeaderboardTabId, LeaderboardTabText> = {
  streak: { label: "Серии", emoji: "🔥" },
  catches: { label: "Охотники", emoji: "🎯" },
  shame: { label: "Лентяи", emoji: "😴" },
  stat: { label: "Характеристика", emoji: "📊" },
};

/**
 * Подпись под табом: что именно показывает метрика.
 *
 * «Серии» — текущая серия дней подряд (после этапа 5 это
 * `memberships.current_streak`, а не сумма всех чек-инов).
 */
export const LEADERBOARD_TAB_DESCRIPTIONS: Record<LeaderboardTabId, string> = {
  streak: "Текущая серия: сколько дней подряд участник отмечается без пропусков.",
  catches: "Сколько раз участник поймал нарушителей в этом клубе.",
  shame: "Сколько дней подряд участник пропустил чек-ин.",
  stat: "Сколько единиц характеристики набрал каждый в этом клубе.",
};

/**
 * Склонение для «поимок» (1 раз, 2 раза, 5 раз). Применяется для catches
 * (label = «раз» — одинаково для 1/2/5, но 21-22 = специальное).
 * «дн.» / «штрафов» — без склонения, статичные.
 */
function pluralRaz(n: number): "раз" | "раза" {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "раз";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "раза";
  return "раз";
}

/**
 * Метрика строки рейтинга с единицей измерения.
 *
 * - `streak` → «N дн.» — текущая серия дней подряд;
 * - `catches` → «N раз/раза» — число поимок;
 * - `shame` → «N штрафов» — число штрафов;
 * - `stat` → «N ед.» — единицы характеристики (консистентно с StatusBadge
 *   «{total} ед.» и FrozenStatBanner «N характеристик»).
 */
export function leaderboardMetricLabel(tab: LeaderboardTabId, value: number): string {
  if (tab === "streak") return `${value} дн.`;
  if (tab === "catches") return `${value} ${pluralRaz(value)}`;
  if (tab === "stat") return `${value} ед.`;
  return `${value} штрафов`;
}
